import React from 'react';
import { Calendar, Check } from 'lucide-react';
import { Plan } from '../../models/plan';
import { formatLimit, getPlanMeterLimit, PLAN_METERS } from '../../utils/plan.utils';

interface PlanCardSelectorProps {
  plans: Plan[];
  selectedPlanCode: string;
  onSelect: (planCode: string) => void;
  disabled?: boolean;
  filterCustom?: boolean;
}

export const PlanCardSelector: React.FC<PlanCardSelectorProps> = ({
  plans,
  selectedPlanCode,
  onSelect,
  disabled = false,
  filterCustom = false,
}) => {
  // Filter plans if needed
  const filteredPlans = filterCustom 
    ? plans.filter(plan => plan.plan_kind !== 'custom')
    : plans;

  if (filteredPlans.length === 0) {
    return (
      <div className="text-center py-4 text-sm text-dark-invirtus/50">
        Loading plans...
      </div>
    );
  }

  return (
    <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
      {filteredPlans.map((plan) => {
        const isSelected = plan.plan_code === selectedPlanCode;
        
        return (
          <button
            key={plan.plan_code}
            type="button"
            onClick={() => !disabled && onSelect(plan.plan_code)}
            disabled={disabled}
            className={`w-full text-left px-3 py-2 rounded-lg border-2 transition-all duration-200 ${
              isSelected
                ? 'border-purple bg-purple/5 shadow-sm'
                : 'border-light-lila/30 bg-white hover:border-purple/50 hover:bg-light-lila/5'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                {/* Plan Name and Type Badge - Inline */}
                <div className="flex items-center gap-1.5 mb-1">
                  <span className={`font-semibold text-xs truncate text-dark-invirtus`}>
                    {plan.name}
                  </span>
                  <span className={`text-xs truncate font-medium text-dark-invirtus`}>
                    - {plan.plan_kind.charAt(0).toUpperCase() + plan.plan_kind.slice(1).toLowerCase()}
                  </span>
                  {isSelected && (
                    <Check className="w-3 h-3 text-purple flex-shrink-0 ml-auto" />
                  )}
                </div>
                
                {/* Plan Details - Compact Horizontal Layout */}
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs text-dark-invirtus/70">
                  {PLAN_METERS.map((meter) => (
                    <span key={meter.key}>
                      {meter.short}: {formatLimit(getPlanMeterLimit(plan, meter.key))}
                    </span>
                  ))}
                  {plan.duration_days && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-dark-invirtus/70" />
                      {plan.duration_days}d
                    </span>
                  )}
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

